import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { BaseTableActions } from './BaseTableActions';

/**
 * BaseCard Component
 *
 * Card view for dedicated knowledge bases.
 * Displays knowledge base information in a card format.
 *
 * @param {Object} props
 * @param {Object} props.base - Knowledge base object
 * @param {Function} props.onEdit - Callback when edit button is clicked
 * @param {Function} props.onDelete - Callback when delete button is clicked
 */
export function BaseCard({ base, onEdit, onDelete }) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleView = () => {
    navigate(`/knowledge-bases/${base.id}`);
  };

  return (
    <div
      className="bg-app border border-border-default rounded-lg p-4 hover-interactive"
      onClick={handleView}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-base font-semibold text-default">
          {base.name || '-'}
        </h3>
        <div onClick={(e) => e.stopPropagation()}>
          <BaseTableActions
            base={base}
            onEdit={onEdit}
            onDelete={onDelete}
          />
        </div>
      </div>

      {/* Description */}
      {base.description && (
        <p className="text-sm text-subtle line-clamp-2 mb-3">
          {base.description}
        </p>
      )}

      {/* Segmentation Info */}
      {base.base_type && base.code && (
        <div className="mb-3">
          <span className="inline-flex items-center px-2.5 py-1 text-xs font-normal text-brand bg-brand/3 rounded-xl">
            {base.base_type === 'COMPANY'
              ? t('knowledgeBases.type.company', 'Company')
              : t('knowledgeBases.type.collectiveAgreement', 'Agreement')}
          </span>
        </div>
      )}

      {/* Footer */}
      <div className="flex items-center justify-between text-sm text-muted pt-3 border-t border-border-default">
        <div className="flex items-center gap-3">
          <span className="flex items-center gap-1">
            <Icon icon="lucide:file-text" className="size-4" />
            {base.file_count || 0}
          </span>
          <span className="flex items-center gap-1">
            <Icon icon="lucide:message-circle-question" className="size-4" />
            {base.faq_count || 0}
          </span>
        </div>
        <Icon icon="lucide:chevron-right" className="size-4" />
      </div>
    </div>
  );
}

BaseCard.propTypes = {
  base: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    name: PropTypes.string,
    description: PropTypes.string,
  }).isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};
